/**
 * Insights (R249, decisions 1048 to 1050; docs/specs/cloud/insights.md): production and productivity across jobs, by
 * project, aircraft and pilot, by week, month and financial year. No money figures (decision 1048).
 *
 * Nothing here adds up km, hours or fuel itself: every total is the Operations Hub's `opsSummary` over a group's rows,
 * so a figure cannot differ between the two pages. What is new is how the rows are grouped, and:
 *
 * - by pilot: a line belongs to the daily entry of its flight (`isEntryOfFlight`), and a flight with two pilots counts
 *   for each, as `pilotSummaries` does; a line with no such entry stays visible under "Pilot not recorded";
 * - refly km: `reflyM` of the day summary; refly rate: refly km ÷ km flown; reasons: `reflyReason` of the Refly list;
 * - first-time acceptance: of the production lines reviewed on their first flight, those accepted in full;
 * - QC pass rate: the newest verdict of each line (`flightLines`' rule), pass only; a warning is not a pass;
 * - days on the job: `projectEstimate`'s, against the estimate in the signed contract's snapshot.
 *
 * Standby stays with the project (decision 230), so grouped by aircraft or pilot there is no standby figure. An
 * aircraft's use stays in days, "flew on N of M", never a percentage (decision 228). Pilots are listed by name, not
 * ranked. Fuel is litres only (decision 234).
 *
 * History jobs (`tjr-history.ts`) come in as the same rows, under their own id, and only ever here. A history job with
 * the number of a live project is set aside: the project's own rows win, so nothing counts twice.
 */
import { isoDate, isoDateToDate, isoWeekMonday, isoWeekOf, addIsoDays } from './dates.js';
import { reflyM } from './day-summary.js';
import { parseLineNumber } from './lines.js';
import type { DailyEntry, IsoDate, Uuid } from './model.js';
import {
  aircraftProjectRows,
  aircraftSpans,
  opsSummary,
  utilisationTotal,
  type AircraftSpan,
  type OpsEntry,
  type OpsLine,
  type OpsRange,
  type StandbyDays,
} from './ops.js';
import { financialYearOf, financialYearRange, periodOf, type Period } from './platform-costs.js';
import { projectEstimate } from './project-estimate.js';
import { formatNumber } from './qc-spec.js';
import type { QcLineResult, QcVerdict } from './geosoft-qc.js';
import type { DurationUnit } from './quote-model.js';
import { reflyReason } from './refly-list.js';
import { entryCrewName, normalisePersonName, personNameKey } from './staff.js';
import { aveDailyAcceptedKm } from './tjr-summary.js';
import { historyOpsRows, jobNumberKey, type HistoryJobRow } from './tjr-history.js';

/**
 * Who opens Insights (decision 1050): tenant admins and managers. This is the one rule for the page (the menu follows
 * it); `insights_can_read` in the database says the same for the history jobs. Office staff keep the Operations Hub.
 * Braynewave sees it only under a support grant, whose context carries the manager's pages.
 */
export const INSIGHTS_ROLES: readonly string[] = ['tenant_admin', 'manager'];

export function canInsights(roles: readonly string[]): boolean {
  return roles.some((r) => INSIGHTS_ROLES.includes(r));
}

/** A line as `ops_line_status` returns it since 20261105180000. */
export interface InsightsLiveLine extends OpsLine {
  readonly id: Uuid;
  readonly flt_num: number | null;
  readonly reason: string;
}

/** A daily entry as `ops_daily_entries` returns it since 20261105180000. */
export type InsightsLiveEntry = OpsEntry &
  Pick<DailyEntry, 'pilot_id' | 'pilot2_id'> &
  Partial<Pick<DailyEntry, 'pilot_name' | 'pilot2_name'>>;

/** One QC result of a line; `tests` only matter on a failed line (the Refly list's reason word). */
export interface InsightsQcRow extends Pick<QcLineResult, 'line_status_id' | 'verdict' | 'created_at'> {
  readonly tests?: QcLineResult['tests'];
}

export interface InsightsInput {
  readonly projects: readonly {
    readonly id: Uuid;
    readonly number: string;
    readonly title: string;
    readonly clientName: string;
  }[];
  readonly aircraft: readonly {
    readonly id: Uuid;
    readonly registration: string;
    readonly typeName: string;
  }[];
  readonly people: readonly { readonly id: Uuid; readonly name: string }[];
  readonly blocks: readonly { readonly project_id: Uuid; readonly trav_spacing_m: number | null }[];
  readonly lines: readonly InsightsLiveLine[];
  readonly entries: readonly InsightsLiveEntry[];
  readonly history: readonly HistoryJobRow[];
  /** Null when the QC results could not be read: never "QC not run". */
  readonly qc: readonly InsightsQcRow[] | null;
}

export type InsightsSource = 'teslyn' | 'history';

export interface InsightsJob {
  readonly id: Uuid;
  readonly number: string;
  readonly title: string;
  readonly clientName: string;
  readonly source: InsightsSource;
  /** Distinct line spacings of its blocks, metres. */
  readonly spacingsM: readonly number[];
}

export interface InsightsLine extends OpsLine {
  readonly id: string;
  readonly flt_num: number | null;
  readonly reason: string;
  /** Pilot keys (`personNameKey`) of its flight's daily entry; none when no entry names a pilot. */
  readonly pilots: readonly string[];
  /** The line's first flight on the job. */
  readonly firstFlight: boolean;
  /** The line's newest QC verdict; null when QC did not cover it. */
  readonly qc: Exclude<QcVerdict, 'not_run'> | null;
  /** The Refly list's reason word, on a rejected or part-accepted line. */
  readonly reflyWord: string | null;
}

export interface InsightsEntry extends OpsEntry {
  readonly pilots: readonly string[];
}

export interface InsightsData {
  readonly jobs: readonly InsightsJob[];
  readonly lines: readonly InsightsLine[];
  readonly entries: readonly InsightsEntry[];
  readonly spans: readonly AircraftSpan[];
  readonly pilotNames: ReadonlyMap<string, string>;
  /** Aircraft type by registration. */
  readonly aircraftTypes: ReadonlyMap<string, string>;
  /** History jobs left out of the figures because a live project has the same number. */
  readonly setAside: readonly HistoryJobRow[];
  readonly qcRead: boolean;
}

/** The aircraft a row names: its registration in capitals, so a job in Teslyn and a TJR job follow one aircraft. */
const regoKey = (rego: string | null | undefined): string | null => {
  const t = (rego ?? '').trim().toUpperCase();
  return t === '' ? null : t;
};

/** Everything Insights shows, as one set of rows, the history jobs among them. */
export function insightsData(input: InsightsInput): InsightsData {
  const pilotNames = new Map<string, string>();
  const named = (name: string | null | undefined): string[] => {
    const n = normalisePersonName(name ?? '');
    if (n === '') return [];
    const key = personNameKey(n);
    if (!pilotNames.has(key)) pilotNames.set(key, n);
    return [key];
  };

  const regoOf = new Map(input.aircraft.map((a) => [a.id, regoKey(a.registration)]));
  const aircraftTypes = new Map<string, string>();
  for (const a of input.aircraft) {
    const k = regoKey(a.registration);
    if (k && a.typeName) aircraftTypes.set(k, a.typeName);
  }

  const qcOf = newestQc(input.qc ?? []);
  const failsOf = new Map<Uuid, InsightsQcRow[]>();
  for (const q of input.qc ?? []) {
    if (q.verdict !== 'fail' || !q.line_status_id) continue;
    failsOf.set(q.line_status_id, [...(failsOf.get(q.line_status_id) ?? []), q]);
  }

  const liveNumbers = new Set(input.projects.map((p) => jobNumberKey(p.number)));
  const counted = input.history.filter((h) => !liveNumbers.has(jobNumberKey(h.number)));
  const setAside = input.history.filter((h) => liveNumbers.has(jobNumberKey(h.number)));

  const spacings = new Map<Uuid, number[]>();
  for (const b of input.blocks) {
    if (b.trav_spacing_m === null || b.trav_spacing_m <= 0) continue;
    const list = spacings.get(b.project_id) ?? [];
    if (!list.includes(b.trav_spacing_m)) list.push(b.trav_spacing_m);
    spacings.set(b.project_id, list);
  }
  const jobs: InsightsJob[] = [
    ...input.projects.map((p): InsightsJob => ({
      id: p.id,
      number: p.number,
      title: p.title,
      clientName: p.clientName,
      source: 'teslyn',
      spacingsM: [...(spacings.get(p.id) ?? [])].sort((a, b) => a - b),
    })),
    ...counted.map((h): InsightsJob => ({
      id: h.id,
      number: h.number,
      title: h.title,
      clientName: h.client_name,
      source: 'history',
      spacingsM: [
        ...new Set(
          h.data.blocks.map((b) => b.line_spacing_m).filter((m): m is number => m !== null && m > 0),
        ),
      ].sort((a, b) => a - b),
    })),
  ];

  // every row names its aircraft by registration from here on
  const liveEntries: InsightsEntry[] = input.entries.map((e) => ({
    ...e,
    aircraft_id: e.aircraft_id ? (regoOf.get(e.aircraft_id) ?? e.aircraft_id) : null,
    pilots: uniq([...crewOf(input.people, e, 'pilot', named), ...crewOf(input.people, e, 'pilot2', named)]),
  }));
  const historyEntries: InsightsEntry[] = [];
  const historyLines: InsightsLiveLine[] = [];
  for (const h of counted) {
    for (const a of h.data.aircraft) {
      const k = regoKey(a.registration);
      if (k && a.type_name && !aircraftTypes.has(k)) aircraftTypes.set(k, a.type_name);
    }
    const rows = historyOpsRows(h.id, h.data);
    rows.entries.forEach((e, i) => {
      historyEntries.push({ ...e, pilots: named(h.data.entries[i]!.pilot_name) });
    });
    rows.lines.forEach((l, i) => {
      const own = h.data.lines[i]!;
      historyLines.push({ ...l, id: `${h.id}:l${i}`, flt_num: own.flt_num, reason: own.reason });
    });
  }
  const entries = [...liveEntries, ...historyEntries];

  const entriesOn = new Map<string, InsightsEntry[]>();
  for (const e of entries) {
    if (e.entry_type !== 'flight' || e.flt_num === null) continue;
    const k = `${e.project_id}|${e.date}|${e.flt_num}`;
    entriesOn.set(k, [...(entriesOn.get(k) ?? []), e]);
  }
  const allLines = [
    ...input.lines.map((l) => ({
      ...l,
      aircraft_id: l.aircraft_id
        ? (regoOf.get(l.aircraft_id) ?? regoKey(l.rego) ?? l.aircraft_id)
        : regoKey(l.rego),
      reason: l.reason ?? '',
      flt_num: l.flt_num ?? null,
    })),
    ...historyLines,
  ];
  const first = firstFlights(allLines);
  const lines: InsightsLine[] = allLines.map((l) => {
    const paired = (entriesOn.get(`${l.project_id}|${l.flight_date}|${l.flt_num}`) ?? []).find(
      (e) => e.aircraft_id === null || l.aircraft_id === null || e.aircraft_id === l.aircraft_id,
    );
    const reflown = l.acceptance === 'partial' || l.acceptance === 'rejected';
    return {
      ...l,
      pilots: paired?.pilots ?? [],
      firstFlight: first.has(l.id),
      qc: qcOf.get(l.id) ?? null,
      reflyWord: reflown
        ? reflyReason(
            l,
            (failsOf.get(l.id) ?? []).map((q) => ({
              deleted_at: null,
              line_status_id: q.line_status_id,
              tests: q.tests ?? [],
            })),
          )
        : null,
    };
  });
  return {
    jobs,
    lines,
    entries,
    spans: aircraftSpans(entries),
    pilotNames,
    aircraftTypes,
    setAside,
    qcRead: input.qc !== null,
  };
}

const uniq = (keys: readonly string[]): string[] => [...new Set(keys)];

function crewOf(
  people: InsightsInput['people'],
  e: InsightsLiveEntry,
  slot: 'pilot' | 'pilot2',
  named: (name: string | null | undefined) => string[],
): string[] {
  return named(
    entryCrewName(
      people,
      { ...e, operator1_id: null, operator2_id: null, pilot2_id: e.pilot2_id ?? null },
      slot,
    ),
  );
}

/** Each line's newest verdict, as the Flights page takes it; a run that did not reach the line says nothing. */
function newestQc(qc: readonly InsightsQcRow[]): Map<Uuid, Exclude<QcVerdict, 'not_run'>> {
  const newest = new Map<Uuid, { verdict: Exclude<QcVerdict, 'not_run'>; at: string }>();
  for (const q of qc) {
    if (!q.line_status_id || q.verdict === 'not_run') continue;
    const had = newest.get(q.line_status_id);
    if (!had || q.created_at > had.at) newest.set(q.line_status_id, { verdict: q.verdict, at: q.created_at });
  }
  return new Map([...newest].map(([id, v]) => [id, v.verdict]));
}

/** The ids of each production line's first record on its job: earliest day, then lowest version. */
function firstFlights(
  lines: readonly Pick<InsightsLine, 'id' | 'project_id' | 'line_number' | 'flight_date'>[],
): Set<string> {
  const best = new Map<string, { id: string; date: IsoDate; version: number }>();
  for (const l of lines) {
    const p = parseLineNumber(l.line_number);
    if (!p.production) continue;
    const k = `${l.project_id}|${p.base}`;
    const had = best.get(k);
    if (!had || l.flight_date < had.date || (l.flight_date === had.date && p.version < had.version)) {
      best.set(k, { id: l.id, date: l.flight_date, version: p.version });
    }
  }
  return new Set([...best.values()].map((b) => b.id));
}

// ---------------------------------------------------------------- the choices above the page

export type InsightsGroup = 'project' | 'aircraft' | 'pilot';
export type InsightsGrain = 'all' | 'week' | 'month' | 'fy';
export type InsightsRangeChoice = 'all' | 'this_fy' | 'last_fy' | 'custom';
export type InsightsSourceChoice = 'all' | InsightsSource;

export const INSIGHTS_GROUP_LABEL: Readonly<Record<InsightsGroup, string>> = {
  project: 'Project',
  aircraft: 'Aircraft',
  pilot: 'Pilot',
};

export const INSIGHTS_GRAIN_LABEL: Readonly<Record<InsightsGrain, string>> = {
  all: 'Whole range',
  week: 'Week',
  month: 'Month',
  fy: 'Financial year',
};

export const INSIGHTS_RANGE_LABEL: Readonly<Record<InsightsRangeChoice, string>> = {
  all: 'All jobs',
  this_fy: 'This financial year',
  last_fy: 'Last financial year',
  custom: 'Custom',
};

export const INSIGHTS_SOURCE_LABEL: Readonly<Record<InsightsSourceChoice, string>> = {
  all: 'Teslyn and history',
  teslyn: 'Jobs in Teslyn',
  history: 'History jobs',
};

export interface InsightsChoice {
  readonly group: InsightsGroup;
  readonly grain: InsightsGrain;
  readonly range: OpsRange;
  readonly source: InsightsSourceChoice;
}

/** The first and last day with a line or an entry; null with none. */
export function insightsSpan(data: Pick<InsightsData, 'lines' | 'entries'>): OpsRange | null {
  const dates = [...data.lines.map((l) => l.flight_date), ...data.entries.map((e) => e.date)].sort();
  return dates.length > 0 ? { from: dates[0]!, to: dates[dates.length - 1]! } : null;
}

/** The dates of a choice; `span` is `insightsSpan`, `today` the viewer's local date. Financial years run July to June. */
export function insightsRange(
  choice: InsightsRangeChoice,
  today: IsoDate,
  span: OpsRange | null,
  custom: OpsRange,
): OpsRange {
  const fy = financialYearOf(today);
  if (choice === 'this_fy') return financialYearRange(fy);
  if (choice === 'last_fy') return financialYearRange(fy - 1);
  if (choice === 'custom') return custom;
  return span ?? custom;
}

/** Why a range cannot be shown, in words; null when it can. Unlike the Hub's, it may run for years. */
export function insightsRangeIssue(r: OpsRange): string | null {
  if (r.from === '') return 'Enter the first day of the range.';
  if (r.to === '') return 'Enter the last day of the range.';
  return r.from > r.to ? 'The first day must be on or before the last day.' : null;
}

/** The week of a date, Monday to Sunday (ISO weeks, as the weekly report). */
function weekOf(iso: IsoDate): Period {
  const w = isoWeekOf(isoDateToDate(iso));
  const from = isoDate(isoWeekMonday(w.isoYear, w.week));
  return {
    key: `${w.isoYear}-W${String(w.week).padStart(2, '0')}`,
    label: `Week ${w.week}, ${w.isoYear}`,
    from,
    to: addIsoDays(from, 6),
  };
}

function periodFor(iso: IsoDate, grain: InsightsGrain, range: OpsRange): Period {
  if (grain === 'all') return { key: 'all', label: 'Whole range', ...range };
  return grain === 'week' ? weekOf(iso) : periodOf(iso, grain);
}

// ---------------------------------------------------------------- the figures of a group

export interface InsightsRate {
  readonly count: number;
  readonly of: number;
  /** Per cent; null when there is nothing to divide by. */
  readonly pct: number | null;
}

export type InsightsQc =
  | { readonly state: 'not_read' }
  | { readonly state: 'not_run' }
  | {
      readonly state: 'ran';
      readonly pass: number;
      readonly warn: number;
      readonly fail: number;
      /** Lines QC covered. */
      readonly lines: number;
      /** Every line of the group, QC or not. */
      readonly ofLines: number;
      /** Pass ÷ the lines QC covered, per cent. */
      readonly pct: number;
    };

export interface InsightsFigures {
  readonly flights: number;
  readonly flightHours: number;
  readonly surveyHours: number;
  readonly surveyDays: number;
  readonly flownKm: number;
  readonly acceptedKm: number;
  /** Accepted km per flight, flight hour, survey hour and survey day; null with nothing to divide by. */
  readonly kmPerFlight: number | null;
  readonly kmPerFlightHour: number | null;
  readonly kmPerSurveyHour: number | null;
  readonly kmPerSurveyDay: number | null;
  readonly fuelUsedL: number;
  readonly reflyKm: number;
  /** Rejected and part-accepted line records, of every line record. */
  readonly reflyLines: number;
  readonly lineRecords: number;
  /** Refly km ÷ km flown, per cent; null with nothing flown. */
  readonly reflyPct: number | null;
  readonly firstTime: InsightsRate;
  readonly qc: InsightsQc;
  readonly standby: StandbyDays;
  readonly maintenanceDays: number;
  readonly suspendedDays: number;
}

const over = (a: number, b: number): number | null => (b > 0 ? a / b : null);

function figuresOf(
  lines: readonly InsightsLine[],
  entries: readonly InsightsEntry[],
  range: OpsRange,
  qcRead: boolean,
): InsightsFigures {
  const s = opsSummary(lines, entries, { ...range, projectId: null, aircraftId: null });
  const reflyKm = reflyM(lines) / 1000;
  const firsts = lines.filter((l) => l.firstFlight && l.acceptance !== 'pending');
  const accepted = firsts.filter((l) => l.acceptance === 'accepted').length;
  return {
    flights: s.flights,
    flightHours: s.flightHours,
    surveyHours: s.surveyHours,
    surveyDays: s.surveyDays,
    flownKm: s.flownKm,
    acceptedKm: s.acceptedKm,
    kmPerFlight: over(s.acceptedKm, s.flights),
    kmPerFlightHour: over(s.acceptedKm, s.flightHours),
    kmPerSurveyHour: s.acceptedKmPerSurveyHour,
    kmPerSurveyDay: s.surveyDays > 0 ? aveDailyAcceptedKm(s.acceptedKm, s.surveyDays) : null,
    fuelUsedL: s.fuelUsedL,
    reflyKm,
    reflyLines: lines.filter((l) => l.reflyWord !== null).length,
    lineRecords: lines.length,
    reflyPct: s.flownKm > 0 ? (reflyKm / s.flownKm) * 100 : null,
    firstTime: {
      count: accepted,
      of: firsts.length,
      pct: firsts.length > 0 ? (accepted / firsts.length) * 100 : null,
    },
    qc: qcFigures(lines, qcRead),
    standby: s.standby,
    maintenanceDays: s.maintenanceDays,
    suspendedDays: s.suspendedDays,
  };
}

function qcFigures(lines: readonly InsightsLine[], qcRead: boolean): InsightsQc {
  if (!qcRead) return { state: 'not_read' };
  const covered = lines.filter((l) => l.qc !== null);
  if (covered.length === 0) return { state: 'not_run' };
  const pass = covered.filter((l) => l.qc === 'pass').length;
  return {
    state: 'ran',
    pass,
    warn: covered.filter((l) => l.qc === 'warn').length,
    fail: covered.filter((l) => l.qc === 'fail').length,
    lines: covered.length,
    ofLines: lines.length,
    pct: (pass / covered.length) * 100,
  };
}

export const INSIGHTS_NO_AIRCRAFT = 'none';
export const INSIGHTS_NO_PILOT = 'none';

export interface InsightsRow extends InsightsFigures {
  /** Unique within the view. */
  readonly key: string;
  readonly groupKey: string;
  readonly label: string;
  /** What makes the row comparable: aircraft type and line spacing (km per hour only compares like with like). */
  readonly context: string;
  readonly history: boolean;
  readonly period: Period;
  /** Aircraft rows only: "flew on N of M" (decision 228); null otherwise. */
  readonly use: { readonly flyingDays: number; readonly projectDays: number } | null;
}

export interface InsightsReason {
  readonly reason: string;
  readonly lines: number;
  readonly km: number;
}

export interface InsightsView {
  /** Every row of the choice once: a total never counts a two-pilot flight twice. */
  readonly total: InsightsFigures;
  readonly rows: readonly InsightsRow[];
  readonly reasons: readonly InsightsReason[];
  /** Accepted km with no pilot recorded, shown so a pilot's total can be trusted. */
  readonly noPilotKm: number;
}

interface Bucket {
  readonly groupKey: string;
  readonly period: Period;
  readonly lines: InsightsLine[];
  readonly entries: InsightsEntry[];
}

const inRange = (d: IsoDate, r: OpsRange): boolean => d >= r.from && d <= r.to;

/** What a line counts under. */
function lineGroups(l: InsightsLine, group: InsightsGroup): readonly string[] {
  if (group === 'project') return [l.project_id];
  if (group === 'aircraft') return [l.aircraft_id ?? INSIGHTS_NO_AIRCRAFT];
  return l.pilots.length > 0 ? l.pilots : [INSIGHTS_NO_PILOT];
}

/** What an entry counts under. Standby stays with the project (decision 230). */
function entryGroups(e: InsightsEntry, group: InsightsGroup): readonly string[] {
  if (group === 'project') return [e.project_id];
  if (e.entry_type !== 'flight') return [];
  if (group === 'aircraft') return [e.aircraft_id ?? INSIGHTS_NO_AIRCRAFT];
  return e.pilots.length > 0 ? e.pilots : [INSIGHTS_NO_PILOT];
}

const SOURCE_ORDER: Readonly<Record<InsightsSource, number>> = { teslyn: 0, history: 1 };

/** The rows, total and refly reasons of a choice. Pilots come in name order, never ranked. */
export function insightsView(data: InsightsData, choice: InsightsChoice): InsightsView {
  const { group, grain, range, source } = choice;
  const jobOf = new Map(data.jobs.map((j) => [j.id, j]));
  const wanted = (projectId: Uuid): boolean => source === 'all' || jobOf.get(projectId)?.source === source;
  const lines = data.lines.filter((l) => inRange(l.flight_date, range) && wanted(l.project_id));
  const entries = data.entries.filter((e) => inRange(e.date, range) && wanted(e.project_id));

  const buckets = new Map<string, Bucket>();
  const bucket = (groupKey: string, date: IsoDate): Bucket => {
    const period = periodFor(date, grain, range);
    const key = `${groupKey}|${period.key}`;
    let b = buckets.get(key);
    if (!b) {
      b = { groupKey, period, lines: [], entries: [] };
      buckets.set(key, b);
    }
    return b;
  };
  for (const l of lines) for (const k of lineGroups(l, group)) bucket(k, l.flight_date).lines.push(l);
  for (const e of entries) for (const k of entryGroups(e, group)) bucket(k, e.date).entries.push(e);

  const named = (b: Bucket): Pick<InsightsRow, 'label' | 'context' | 'history'> & { order: string } => {
    if (group === 'project') {
      const job = jobOf.get(b.groupKey);
      return {
        label: job ? `${job.number} ${job.title}`.trim() : 'Unknown project',
        context: jobContext(data, job, lines, entries),
        history: job?.source === 'history',
        order: `${SOURCE_ORDER[job?.source ?? 'teslyn']}|${job?.number ?? ''}`,
      };
    }
    if (group === 'aircraft') {
      const none = b.groupKey === INSIGHTS_NO_AIRCRAFT;
      return {
        label: none ? 'No aircraft recorded' : b.groupKey,
        context: data.aircraftTypes.get(b.groupKey) ?? '',
        history: false,
        order: none ? '~' : b.groupKey,
      };
    }
    const none = b.groupKey === INSIGHTS_NO_PILOT;
    const name = data.pilotNames.get(b.groupKey) ?? b.groupKey;
    return {
      label: none ? 'Pilot not recorded' : name,
      context: '',
      history: false,
      order: none ? '~' : name.toLowerCase(),
    };
  };

  const rows = [...buckets.values()]
    .map((b) => {
      const { order, ...name } = named(b);
      return {
        order,
        row: {
          ...figuresOf(b.lines, b.entries, overlap(b.period, range), data.qcRead),
          ...name,
          key: `${b.groupKey}|${b.period.key}`,
          groupKey: b.groupKey,
          period: b.period,
          use: group === 'aircraft' ? aircraftUse(data, b.groupKey, overlap(b.period, range), wanted) : null,
        } satisfies InsightsRow,
      };
    })
    .sort((a, b) => a.order.localeCompare(b.order) || a.row.period.key.localeCompare(b.row.period.key))
    .map((x) => x.row);

  const byReason = new Map<string, { lines: number; km: number }>();
  for (const l of lines) {
    if (l.reflyWord === null) continue;
    const r = byReason.get(l.reflyWord) ?? { lines: 0, km: 0 };
    r.lines += 1;
    r.km += reflyM([l]) / 1000;
    byReason.set(l.reflyWord, r);
  }
  return {
    total: figuresOf(lines, entries, range, data.qcRead),
    rows,
    reasons: [...byReason.entries()]
      .map(([reason, r]) => ({ reason, ...r }))
      .sort((a, b) => b.km - a.km || a.reason.localeCompare(b.reason)),
    noPilotKm:
      lines
        .filter((l) => l.pilots.length === 0)
        .reduce((a, l) => a + (l.acceptance !== 'pending' && l.use_km ? l.accepted_m : 0), 0) / 1000,
  };
}

/** A period cut to the range, so a part week counts only the days asked for. */
const overlap = (p: Period, r: OpsRange): OpsRange => ({
  from: p.from > r.from ? p.from : r.from,
  to: p.to < r.to ? p.to : r.to,
});

/** "Cessna Grand Caravan · 100 m lines". */
function jobContext(
  data: InsightsData,
  job: InsightsJob | undefined,
  lines: readonly InsightsLine[],
  entries: readonly InsightsEntry[],
): string {
  if (!job) return '';
  const regos = new Set<string>();
  for (const l of lines) if (l.project_id === job.id && l.aircraft_id) regos.add(l.aircraft_id);
  for (const e of entries) if (e.project_id === job.id && e.aircraft_id) regos.add(e.aircraft_id);
  const types = uniq([...regos].flatMap((r) => data.aircraftTypes.get(r) ?? []));
  const spacing =
    job.spacingsM.length > 0 ? `${job.spacingsM.map((m) => formatNumber(m)).join(', ')} m lines` : '';
  return [...types, spacing].filter((s) => s !== '').join(' · ');
}

function aircraftUse(
  data: InsightsData,
  aircraft: string,
  range: OpsRange,
  wanted: (projectId: Uuid) => boolean,
): { readonly flyingDays: number; readonly projectDays: number } {
  const entries = data.entries.filter((e) => e.aircraft_id === aircraft && wanted(e.project_id));
  const spans = data.spans.filter((s) => s.aircraft_id === aircraft && wanted(s.project_id));
  const use = utilisationTotal(
    aircraftProjectRows([], entries, { ...range, projectId: null, aircraftId: aircraft }, spans),
  );
  return { flyingDays: use.flyingDays, projectDays: use.projectDays };
}

// ---------------------------------------------------------------- days on the job against the contract

/** A signed or closed contract with the duration in its snapshot (the quote's "estimated duration"). */
export interface InsightsContract {
  readonly project_id: Uuid | null;
  readonly number: string;
  readonly status: string;
  readonly signed_at: string | null;
  readonly created_at: string;
  readonly est_duration_value: number | null;
  readonly est_duration_unit: DurationUnit | null;
}

const DAYS_PER: Readonly<Record<DurationUnit, number>> = { days: 1, weeks: 7, months: 30 };

/** A quote's estimated duration in days; null when it has none. A month is 30 days. */
export function estimatedDays(
  value: number | null | undefined,
  unit: DurationUnit | null | undefined,
): number | null {
  if (value === null || value === undefined || !(value > 0)) return null;
  return value * DAYS_PER[unit ?? 'days'];
}

/** The contract the Hub reads for a project (`ops_projects`): signed or closed, latest signed, then latest made. */
function contractOf(project: Uuid, contracts: readonly InsightsContract[]): InsightsContract | null {
  return (
    contracts
      .filter((c) => c.project_id === project && (c.status === 'signed' || c.status === 'closed'))
      .sort(
        (a, b) =>
          (b.signed_at ?? '').localeCompare(a.signed_at ?? '') || b.created_at.localeCompare(a.created_at),
      )[0] ?? null
  );
}

export interface InsightsDaysRow {
  readonly jobId: Uuid;
  readonly label: string;
  readonly contract: string | null;
  readonly estimatedDays: number | null;
  /** `projectEstimate`'s days on the job, to date; null until it has its three flying days. */
  readonly daysOnJob: number | null;
  /** Days on the job less the estimate; plus is over. */
  readonly differenceDays: number | null;
  /** Why a figure is missing, in words; '' when both are there. */
  readonly note: string;
}

/**
 * Days on the job against the contract's estimate, job to date: one row per job in Teslyn with daily entries. History
 * jobs have no contract in Teslyn, so they are not here. The range does not narrow this table (as the Hub's progress).
 */
export function insightsDaysOnJob(
  data: InsightsData,
  contracts: readonly InsightsContract[],
): InsightsDaysRow[] {
  return data.jobs
    .filter((j) => j.source === 'teslyn')
    .flatMap((job) => {
      const entries = data.entries.filter((e) => e.project_id === job.id);
      if (entries.length === 0) return [];
      const lines = data.lines.filter((l) => l.project_id === job.id);
      const contract = contractOf(job.id, contracts);
      const est = estimatedDays(contract?.est_duration_value, contract?.est_duration_unit);
      const e = projectEstimate(job.id, lines, entries, 0);
      const daysOnJob = e.ready ? e.daysOnJob : null;
      const note = !contract
        ? 'No contract linked.'
        : est === null
          ? 'The contract has no estimated duration.'
          : e.ready
            ? ''
            : e.reason;
      return [
        {
          jobId: job.id,
          label: `${job.number} ${job.title}`.trim(),
          contract: contract?.number ?? null,
          estimatedDays: est,
          daysOnJob,
          differenceDays: est !== null && daysOnJob !== null ? daysOnJob - est : null,
          note,
        },
      ];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
